/**
 * useTimeline — wrap anime.js v4's createTimeline() for sequenced
 * multi-step scenes (Hero reveal, ThreeDoors orchestration, etc.).
 *
 * The hook lazily creates a Timeline on first build() call and exposes
 * it for chaining .add() / .label() / .call() / .sync() calls. Each
 * call to build() tears down the previous timeline so re-builds (e.g.
 * after a route change or prop update) don't leak.
 *
 * Reduced-motion: build() returns null and the caller should snap
 * targets to their final state directly, OR use the optional `onSkip`
 * callback to apply final styles in one place.
 *
 * Example:
 *   const { build } = useTimeline({ ease: EASE.soft });
 *   useEffect(() => {
 *     const tl = build();
 *     if (!tl) return;
 *     tl.add(headlineRef.current, { opacity: [0, 1], translateY: [16, 0] })
 *       .add(ctaRef.current, { opacity: [0, 1] }, '-=200');
 *   }, [build]);
 */

import { useCallback, useEffect, useRef } from "react";
import {
  createTimeline,
  type Timeline,
  type TimelineParams,
} from "animejs";
import { prefersReducedMotion } from "./prefersReducedMotion";

export interface UseTimelineOptions extends TimelineParams {
  /**
   * Called instead of building a timeline when prefers-reduced-motion
   * is on. Use it to apply the final state of every target so the
   * scene is visible without motion.
   */
  onSkip?: () => void;
}

export interface UseTimelineResult {
  /** A ref the caller may attach to a scene root for scoping. Optional to use. */
  ref: React.RefObject<HTMLElement>;
  /** The active timeline (null until build() runs or after reduced-motion skip). */
  timelineRef: React.MutableRefObject<Timeline | null>;
  /**
   * Build (or rebuild) the timeline. Returns the Timeline instance, or
   * null if reduced motion caused the build to skip. Calling build()
   * again pauses the previous timeline before creating a new one.
   */
  build: () => Timeline | null;
}

export function useTimeline(options: UseTimelineOptions = {}): UseTimelineResult {
  const ref = useRef<HTMLElement>(null);
  const timelineRef = useRef<Timeline | null>(null);
  // Hold latest options without re-creating build() on every render.
  const optionsRef = useRef<UseTimelineOptions>(options);
  optionsRef.current = options;

  const build = useCallback((): Timeline | null => {
    const { onSkip, ...params } = optionsRef.current;

    if (prefersReducedMotion()) {
      timelineRef.current?.pause();
      timelineRef.current = null;
      onSkip?.();
      return null;
    }

    timelineRef.current?.pause();
    const tl = createTimeline(params);
    timelineRef.current = tl;
    return tl;
  }, []);

  useEffect(() => {
    return () => {
      timelineRef.current?.pause();
      timelineRef.current = null;
    };
  }, []);

  return { ref, timelineRef, build };
}
